// src/components/admin/QuickActions.tsx
import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/core/lib/utils';
import { QuickAction } from '@/types/admin';

interface QuickActionsProps {
  pendingModeration?: number;
  pendingVendors?: number;
  className?: string;
}

export const QuickActions: React.FC<QuickActionsProps> = ({
  pendingModeration = 0,
  pendingVendors = 0,
  className,
}) => {
  const actions: QuickAction[] = [
    {
      id: 'moderate',
      label: 'Review Content',
      description: `${pendingModeration} items pending`,
      icon: 'moderate',
      href: '/admin/moderation',
      badge: pendingModeration > 0 ? pendingModeration : undefined,
      badgeColor: pendingModeration > 5 ? 'red' : 'yellow',
    },
    {
      id: 'verify-vendors',
      label: 'Verify Vendors',
      description: `${pendingVendors} pending verification`,
      icon: 'verify',
      href: '/admin/vendors',
      badge: pendingVendors > 0 ? pendingVendors : undefined,
      badgeColor: 'purple',
    },
    {
      id: 'add-resource',
      label: 'Add Resource',
      description: 'Create new article or guide',
      icon: 'add',
      href: '/admin/resources/new',
    },
    {
      id: 'view-analytics',
      label: 'View Analytics',
      description: 'Platform performance',
      icon: 'chart',
      href: '/admin/analytics',
    },
    {
      id: 'manage-users',
      label: 'Manage Users',
      description: 'User administration',
      icon: 'users',
      href: '/admin/users',
    },
    {
      id: 'system-status',
      label: 'System Status',
      description: 'Health & monitoring',
      icon: 'system',
      href: '/admin/system',
    },
  ];

  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'moderate':
        return (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
        );
      case 'verify':
        return (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
          </svg>
        );
      case 'add':
        return (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
        );
      case 'chart':
        return (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        );
      case 'users':
        return (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        );
      case 'system':
        return (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
          </svg>
        );
      default:
        return null;
    }
  };

  const getBadgeColor = (color?: string) => {
    switch (color) {
      case 'red':
        return 'bg-red-500';
      case 'yellow':
        return 'bg-yellow-500';
      case 'purple':
        return 'bg-purple-500';
      default:
        return 'bg-sage';
    }
  };

  return (
    <div className={cn('bg-white rounded-xl border border-gray-200 p-6', className)}>
      <h3 className="text-lg font-semibold text-navy mb-4">Quick Actions</h3>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {actions.map((action) => (
          <Link
            key={action.id}
            to={action.href}
            className="relative flex flex-col items-center p-4 rounded-xl bg-cream hover:bg-cream-dark transition-colors group"
          >
            {action.badge !== undefined && (
              <span className={cn(
                'absolute top-2 right-2 w-5 h-5 rounded-full text-white text-xs font-bold flex items-center justify-center',
                getBadgeColor(action.badgeColor)
              )}>
                {action.badge > 99 ? '99+' : action.badge}
              </span>
            )}
            <div className="p-2 rounded-lg bg-white shadow-sm mb-2 text-sage group-hover:text-sage-dark transition-colors">
              {getIcon(action.icon)}
            </div>
            <span className="text-sm font-medium text-navy text-center">{action.label}</span>
            <span className="text-xs text-warm-gray text-center mt-0.5">{action.description}</span>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default QuickActions;
